import { supabase } from '@/lib/supabaseClient';
import { 
  getDiagramCache, 
  setDiagramCache, 
  getDiagramContentCache, 
  setDiagramContentCache,
  getCacheOrFetch,
  DIAGRAM_CACHE_KEYS,
  shouldRefreshCache,
  setCache
} from '@/utils/diagramCacheService';

// Định nghĩa các nguồn dữ liệu có thể có
export enum DataSource {
  LOCAL = 'local',    // Dữ liệu từ local JSON files
  REMOTE = 'remote',  // Dữ liệu từ API
  CUSTOM = 'custom',  // Dữ liệu từ nguồn tùy chỉnh (ví dụ: localStorage)
  DATABASE = 'database' // Dữ liệu từ Supabase database
}

// Kiểu dữ liệu cho diagram
export interface DiagramData {
  id: string;
  name: string;
  description: string;
  type: string;
  image: string;
  details: {
    title: string;
    banner_image?: string;
    content: {
      introduction: string;
      classification_basis?: string[];
      benefits?: string[];
      illustration_image?: string;
    }
  };
  children: any[];
  [key: string]: any;
}

// Cấu hình cho DiagramService
export interface DiagramServiceConfig {
  defaultSource: DataSource;
  remoteBaseUrl?: string;
  localBasePath?: string;
  customLoader?: (id: string) => Promise<DiagramData>;
  catalogLoader?: () => Promise<any[]>;
}

// Cấu hình mặc định
const defaultConfig: DiagramServiceConfig = {
  defaultSource: DataSource.DATABASE, // Mặc định sử dụng database
  localBasePath: '/data/diagrams',
  remoteBaseUrl: '/api'
};

class DiagramService {
  private config: DiagramServiceConfig;
  private lastCacheCheck: number = 0;

  constructor(config: Partial<DiagramServiceConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Cập nhật cấu hình
   */
  updateConfig(config: Partial<DiagramServiceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Lấy danh sách tất cả diagrams
   */
  async getCatalog(source?: DataSource, forceRefresh = false): Promise<any[]> {
    try {
      // Kiểm tra xem có cần làm mới cache không
      if (!forceRefresh && shouldRefreshCache(this.lastCacheCheck)) {
        forceRefresh = true;
      }
      
      this.lastCacheCheck = Date.now();

      // Sử dụng nguồn được chỉ định hoặc nguồn mặc định
      const dataSource = source || this.config.defaultSource;

      // Nếu không yêu cầu refresh, kiểm tra cache
      if (!forceRefresh) {
        const cachedCatalog = getCacheOrFetch(
          DIAGRAM_CACHE_KEYS.ALL_DIAGRAMS,
          async () => {
            switch (dataSource) {
              case DataSource.DATABASE:
                return this.fetchDatabaseCatalog();
              case DataSource.REMOTE:
                return this.fetchRemoteCatalog();
              case DataSource.CUSTOM:
                if (!this.config.catalogLoader) {
                  throw new Error('Custom catalog loader is required for CUSTOM data source');
                }
                return this.config.catalogLoader();
              case DataSource.LOCAL:
              default:
                return this.loadLocalCatalog();
            }
          }
        );
        
        return cachedCatalog;
      } else {
        // Nếu yêu cầu refresh, fetch dữ liệu mới
        let catalog: any[];
        
        switch (dataSource) {
          case DataSource.DATABASE:
            catalog = await this.fetchDatabaseCatalog();
            break;
          case DataSource.REMOTE:
            catalog = await this.fetchRemoteCatalog();
            break;
          case DataSource.CUSTOM:
            if (!this.config.catalogLoader) {
              throw new Error('Custom catalog loader is required for CUSTOM data source');
            }
            catalog = await this.config.catalogLoader();
            break;
          case DataSource.LOCAL:
          default:
            catalog = await this.loadLocalCatalog();
            break;
        }
        
        // Cập nhật cache - sử dụng setCache thay vì setDiagramCache
        setCache(DIAGRAM_CACHE_KEYS.ALL_DIAGRAMS, catalog);
        
        return catalog;
      }
    } catch (error) {
      console.error('Error loading diagram catalog:', error);
      throw error;
    }
  }

  /**
   * Lấy dữ liệu chi tiết của một diagram theo ID
   */
  async getDiagramById(id: string, source?: DataSource, forceRefresh = false): Promise<DiagramData> {
    try {
      console.log(`DiagramService.getDiagramById: Started with ID "${id}", source=${source}, forceRefresh=${forceRefresh}`);
      
      // Kiểm tra xem có cần làm mới cache không
      if (!forceRefresh && shouldRefreshCache(this.lastCacheCheck)) {
        console.log(`DiagramService.getDiagramById: Cache expiration detected, forcing refresh`);
        forceRefresh = true;
      }
      
      this.lastCacheCheck = Date.now();

      // Nếu không yêu cầu refresh, kiểm tra cache
      if (!forceRefresh) {
        console.log(`DiagramService.getDiagramById: Checking cache for ID "${id}"`);
        const cachedDiagram = getDiagramCache(id);
        const cachedContent = getDiagramContentCache(id);
        
        if (cachedDiagram && cachedContent) {
          console.log(`DiagramService.getDiagramById: Cache hit for ID "${id}"`);
          return {
            ...cachedDiagram,
            ...cachedContent.content
          };
        }
        console.log(`DiagramService.getDiagramById: Cache miss for ID "${id}"`);
      } else {
        console.log(`DiagramService.getDiagramById: Bypassing cache due to forceRefresh=${forceRefresh}`);
      }

      // Sử dụng nguồn được chỉ định hoặc nguồn mặc định
      const dataSource = source || this.config.defaultSource;
      console.log(`DiagramService.getDiagramById: Using data source: ${dataSource}`);
      
      let diagram: DiagramData;

      switch (dataSource) {
        case DataSource.DATABASE:
          diagram = await this.fetchDatabaseDiagram(id);
          break;
        case DataSource.REMOTE:
          diagram = await this.fetchRemoteDiagram(id);
          break;
        case DataSource.CUSTOM:
          if (!this.config.customLoader) {
            throw new Error('Custom loader is required for CUSTOM data source');
          }
          diagram = await this.config.customLoader(id);
          break;
        case DataSource.LOCAL:
        default:
          diagram = await this.loadLocalDiagram(id);
          break;
      }

      console.log(`DiagramService.getDiagramById: Successfully retrieved diagram for ID "${id}"`);
      return diagram;
    } catch (error) {
      console.error(`DiagramService.getDiagramById: Error loading diagram ${id}:`, error);
      throw error;
    }
  }

  /**
   * Load catalog từ local JSON file
   */
  private async loadLocalCatalog(): Promise<any[]> {
    try {
      const response = await fetch('/data/diagrams/diagram-catalog.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch diagram catalog: ${response.statusText}`);
      }
      const data = await response.json();
      return data.diagrams || [];
    } catch (error) {
      console.error('Error loading local diagram catalog:', error);
      throw error;
    }
  }

  /**
   * Load diagram từ local JSON file
   */
  private async loadLocalDiagram(id: string): Promise<DiagramData> {
    try {
      // Sử dụng fetch thay vì import trực tiếp
      try {
        const response = await fetch(`/data/diagrams/${id}.json`);
        if (!response.ok) {
          console.warn(`Không tìm thấy file diagram ${id}.json, trả về đối tượng rỗng`);
          return this.createEmptyDiagram(id);
        }
        return await response.json();
      } catch (fetchError) {
        console.warn(`Không thể fetch file diagram ${id}.json, trả về đối tượng rỗng:`, fetchError);
        return this.createEmptyDiagram(id);
      }
    } catch (error) {
      console.error(`Error loading local diagram ${id}:`, error);
      throw error;
    }
  }

  /**
   * Tạo đối tượng diagram rỗng
   */
  private createEmptyDiagram(id: string): DiagramData {
    return {
      id,
      name: `Diagram ${id} (not found)`,
      description: 'This diagram data could not be loaded from JSON files',
      type: 'unknown',
      image: '',
      details: {
        title: `Diagram ${id} (not found)`,
        content: {
          introduction: 'This diagram data could not be loaded from JSON files. Please check the database.'
        }
      },
      children: []
    } as DiagramData;
  }

  /**
   * Fetch catalog từ remote API
   */
  private async fetchRemoteCatalog(): Promise<any[]> {
    try {
      if (!this.config.remoteBaseUrl) {
        throw new Error('Remote base URL is not configured');
      }

      const response = await fetch(`${this.config.remoteBaseUrl}/diagrams`);
      if (!response.ok) {
        throw new Error(`Failed to fetch diagram catalog: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching remote diagram catalog:', error);
      throw error;
    }
  }

  /**
   * Fetch diagram từ remote API
   */
  private async fetchRemoteDiagram(id: string): Promise<DiagramData> {
    try {
      if (!this.config.remoteBaseUrl) {
        throw new Error('Remote base URL is not configured');
      }

      const response = await fetch(`${this.config.remoteBaseUrl}/diagrams/${id}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch diagram ${id}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching remote diagram ${id}:`, error);
      throw error;
    }
  }

  /**
   * Fetch catalog từ database
   */
  private async fetchDatabaseCatalog(): Promise<any[]> {
    try {
      // Lấy danh sách các danh mục có content_type là 'diagram' hoặc 'all'
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .or('content_type.eq.diagram,content_type.eq.all');
        
      if (categoriesError) {
        console.error('Error fetching categories from database:', categoriesError);
      }
      
      // Tạo map các danh mục hợp lệ để dễ dàng truy cập
      const validCategories = new Map();
      categories?.forEach(category => {
        validCategories.set(category.id, category);
      });
      
      // Lấy danh sách diagrams
      const { data, error } = await supabase
        .from('diagrams')
        .select('*, category:category_id(*)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching diagrams from database:', error);
        console.warn('Fallback to local JSON for catalog due to database error');
        // Fallback về local JSON nếu database không khả dụng
        return this.loadLocalCatalog();
      }

      // Chuyển đổi dữ liệu để phù hợp với cấu trúc cũ
      const formattedData = data?.map(item => {
        // Kiểm tra xem category_id có thuộc danh sách danh mục hợp lệ không
        const isValidCategory = validCategories.has(item.category_id);
        
        return {
          ...item,
          category_id: item.category_id,
          // Chỉ lấy category_name nếu danh mục hợp lệ
          category_name: isValidCategory ? item.category?.name : null,
          // Loại bỏ trường category để tránh xung đột
          category: undefined
        };
      }) || [];

      return formattedData;
    } catch (error) {
      console.error('Error fetching diagrams from database:', error);
      console.warn('Fallback to local JSON for catalog due to exception');
      // Fallback về local JSON nếu database không khả dụng
      return this.loadLocalCatalog();
    }
  }

  /**
   * Fetch diagram từ database
   */
  private async fetchDatabaseDiagram(id: string): Promise<DiagramData> {
    try {
      console.log(`DiagramService: Fetching diagram with ID "${id}" from database`);
      
      // Lấy danh sách các danh mục có content_type là 'diagram' hoặc 'all'
      const { data: categories, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .or('content_type.eq.diagram,content_type.eq.all');
        
      if (categoriesError) {
        console.error('DiagramService: Error fetching categories from database:', categoriesError);
      }
      
      // Tạo map các danh mục hợp lệ để dễ dàng truy cập
      const validCategories = new Map();
      categories?.forEach(category => {
        validCategories.set(category.id, category);
      });
      
      // Tìm diagram với ID chính xác
      console.log(`DiagramService: Searching for diagram with exact ID "${id}"`);
      
      const { data: diagram, error } = await supabase
        .from('diagrams')
        .select('*, category:category_id(*)')
        .eq('id', id)
        .single();
        
      // Nếu không tìm thấy diagram
      if (error) {
        console.error(`DiagramService: Could not find diagram with ID "${id}"`, error);
        
        // Throw error cụ thể về việc không tìm thấy
        throw new Error(`Diagram with ID ${id} not found in database`);
      }

      console.log(`DiagramService: Found diagram with ID "${id}"`);

      // Kiểm tra xem category_id có thuộc danh sách danh mục hợp lệ không
      const isValidCategory = validCategories.has(diagram.category_id);
      
      // Chuyển đổi dữ liệu để phù hợp với cấu trúc cũ
      const formattedDiagram = {
        ...diagram,
        category_id: diagram.category_id,
        // Chỉ lấy category_name nếu danh mục hợp lệ
        category_name: isValidCategory ? diagram.category?.name : null,
        // Loại bỏ trường category để tránh xung đột
        category: undefined
      };

      // Lưu vào cache
      setDiagramCache(id, formattedDiagram);

      // Lấy nội dung diagram
      console.log(`DiagramService: Fetching diagram content for diagram.id="${diagram.id}"`);
      const { data: contentData, error: contentError } = await supabase
        .from('diagram_contents')
        .select('*')
        .eq('diagram_id', diagram.id)
        .single();

      if (contentError) {
        console.error(`DiagramService: Error fetching diagram content for ${diagram.id} from database:`, contentError);
        
        // Nếu lỗi là không tìm thấy dữ liệu, throw một error cụ thể
        if (contentError.code === 'PGRST116') {
          throw new Error(`Diagram content with ID ${diagram.id} not found in database`);
        }
        
        // Nếu là lỗi khác, throw error từ database
        throw contentError;
      }

      // Lưu vào cache
      setDiagramContentCache(id, contentData);

      // Kết hợp dữ liệu
      const fullDiagram = {
        ...formattedDiagram,
        ...contentData.content
      };

      console.log(`DiagramService: Successfully loaded diagram with ID "${id}" from database`);
      
      // Cập nhật category_name cho tất cả các node con trong diagram
      const updateCategoryForChildren = (node: any) => {
        if (node.children && node.children.length > 0) {
          node.children = node.children.map((child: any) => {
            // Nếu node con có cùng category_id với node cha, thêm category_name từ node cha
            if (child.category_id === node.category_id && node.category_name) {
              child.category_name = node.category_name;
            }
            // Hoặc nếu node con không có category_id, sử dụng category_id và category_name của node cha
            else if (!child.category_id && node.category_id) {
              child.category_id = node.category_id;
              child.category_name = node.category_name;
            }
            
            // Xử lý đệ quy các node con
            if (child.children && child.children.length > 0) {
              return updateCategoryForChildren(child);
            }
            
            return child;
          });
        }
        
        return node;
      };
      
      // Cập nhật category_name cho tất cả các node con
      return updateCategoryForChildren(fullDiagram);
    } catch (error) {
      console.error(`DiagramService: Error fetching diagram ${id} from database:`, error);
      
      // Throw error để component xử lý
      throw error;
    }
  }
}

// Export singleton instance
export const diagramService = new DiagramService();

// Export class for testing or custom instances
export default DiagramService;

/**
 * Lấy danh sách tất cả các diagrams
 */
export async function getDiagrams(): Promise<DiagramData[]> {
  try {
    // Nếu sử dụng database
    const { data, error } = await supabase
      .from('diagrams')
      .select('*, category:category_id(*)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching diagrams:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getDiagrams:', error);
    throw error;
  }
} 